import { DependencyList, useEffect } from 'react';
import { PixelCrop } from 'react-image-crop';

import { TO_RADIANS } from '@components/ModalComponents/OrganisationForm/BasicInfo/UploadLogo/constants';

export const getBase64 = (blob: Blob) => {
	const reader = new FileReader();

	reader.readAsDataURL(blob);

	return new Promise(resolve => {
		reader.onloadend = () => {
			resolve(reader.result?.toString() || '');
		};
	});
};

export const generateCanvas = ({
	completedCrop,
	previewCanvas,
}: {
	completedCrop: PixelCrop;
	previewCanvas: HTMLCanvasElement;
}) => {
	const offscreen = new OffscreenCanvas(
		completedCrop.width,
		completedCrop.height,
	);

	const ctx = offscreen.getContext('2d');
	if (!ctx) {
		throw new Error('No 2d context');
	}

	ctx.drawImage(
		previewCanvas,
		0,
		0,
		previewCanvas.width,
		previewCanvas.height,
		0,
		0,
		offscreen.width,
		offscreen.height,
	);

	return offscreen;
};

export const useDebounceEffect = (
	fn: () => void,
	waitTime: number,
	deps?: DependencyList,
) => {
	useEffect(() => {
		const t = setTimeout(() => {
			// eslint-disable-next-line prefer-spread
			fn.apply(undefined, deps as any);
		}, waitTime);

		return () => {
			clearTimeout(t);
		};
		// eslint-disable-next-line
	}, deps);
};

export const canvasPreview = async (
	image: HTMLImageElement,
	canvas: HTMLCanvasElement,
	crop: PixelCrop,
	scale = 1,
	rotate = 0,
) => {
	const ctx = canvas.getContext('2d');

	if (!ctx) {
		throw new Error('Error while generating preview: No 2d context');
	}

	const scaleX = image.naturalWidth / image.width;
	const scaleY = image.naturalHeight / image.height;
	// devicePixelRatio slightly increases sharpness on retina devices
	// at the expense of slightly slower render times and needing to
	// size the image back down if you want to download/upload and be
	// true to the images natural size.
	const pixelRatio = window.devicePixelRatio;

	canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
	canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

	ctx.scale(pixelRatio, pixelRatio);
	ctx.imageSmoothingQuality = 'high';

	const cropX = crop.x * scaleX;
	const cropY = crop.y * scaleY;

	const rotateRads = rotate * TO_RADIANS;
	const centerX = image.naturalWidth / 2;
	const centerY = image.naturalHeight / 2;

	ctx.save();

	// 5) Move the crop origin to the canvas origin (0,0)
	ctx.translate(-cropX, -cropY);
	// 4) Move the origin to the center of the original position
	ctx.translate(centerX, centerY);
	// 3) Rotate around the origin
	ctx.rotate(rotateRads);
	// 2) Scale the image
	ctx.scale(scale, scale);
	// 1) Move the center of the image to the origin (0,0)
	ctx.translate(-centerX, -centerY);
	ctx.drawImage(
		image,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight,
	);

	ctx.restore();
};
